import { CloudDownload } from "@mui/icons-material";
import { Alert, Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePurchaseInvoices } from "../../api/invoices";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { ERROR_SNACKBAR, INFO_SNACKBAR, SUCCESS_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";
import { PurchaseInvoiceDownloadStatus } from "../../types/invoices";
import { Action } from "../../types/toolbar";

const STATUS_POLL_INTERVAL = 5000;

export const PurchaseInvoicesListActions = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canDownload = useHasPermission(
    "invoice.download_purchase_invoices_from_ksef"
  );
  const { getDownloadStatus, downloadFromKsef } = usePurchaseInvoices();
  const [status, setStatus] = useState<PurchaseInvoiceDownloadStatus | null>(
    null
  );
  const [starting, setStarting] = useState<boolean>(false);
  // Whether the last status this page saw had a download running. The download
  // happens in the background, so the transition from running to not is the
  // only signal that it finished.
  const wasInProgress = useRef<boolean>(false);

  const fetchStatus = useCallback(async () => {
    const data = await getDownloadStatus();
    if (!data) {
      return;
    }

    if (wasInProgress.current && !data.in_progress) {
      window.dispatchEvent(new Event("purchaseInvoicesDownloaded"));
      if (data.last_error) {
        enqueueSnackbar(t("Downloading invoices from KSeF failed"), ERROR_SNACKBAR);
      } else if (data.resumes_at) {
        enqueueSnackbar(
          t(
            "Some invoices have been downloaded. The rest will follow automatically once the KSeF request limit allows."
          ),
          INFO_SNACKBAR
        );
      } else {
        enqueueSnackbar(t("Invoices have been downloaded from KSeF"), SUCCESS_SNACKBAR);
      }
    }

    wasInProgress.current = data.in_progress;
    setStatus(data);
  }, [getDownloadStatus, enqueueSnackbar, t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!status?.in_progress) {
      return;
    }
    const timer = window.setInterval(fetchStatus, STATUS_POLL_INTERVAL);
    return () => window.clearInterval(timer);
  }, [status?.in_progress, fetchStatus]);

  const handleDownload = async () => {
    setStarting(true);
    const result = await downloadFromKsef();
    setStarting(false);

    if (result?.success && result.data) {
      enqueueSnackbar(t("Downloading invoices from KSeF has started"), INFO_SNACKBAR);
      wasInProgress.current = result.data.in_progress;
      setStatus(result.data);
      return;
    }

    enqueueSnackbar(
      result?.errorData?.message ||
        t("Could not start downloading invoices from KSeF"),
      ERROR_SNACKBAR
    );
  };

  const resumeScheduled =
    !!status?.resumes_at && new Date(status.resumes_at) > new Date();

  const actions: Action[] = [
    {
      id: "download-purchase-invoices-from-ksef",
      icon: <CloudDownload />,
      label: status?.in_progress
        ? t("Downloading from KSeF...")
        : t("Download from KSeF"),
      onClick: handleDownload,
      disabled:
        !canDownload ||
        !status ||
        !status.configured ||
        status.in_progress ||
        resumeScheduled ||
        starting,
    },
  ];

  const downloadState = status && (
    <Box component="fieldset" sx={serviceActionsSectionStyle}>
      <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
        KSeF
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1 }}>
        {!status.configured ? (
          <Typography variant="body2" color="text.secondary">
            {t(
              "Set the company NIP and a KSeF token or certificate in the workspace settings to download invoices."
            )}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {status.synced_until
              ? t("Downloaded up to {{date}}", {
                  date: new Date(status.synced_until).toLocaleString(),
                })
              : t("No invoices have been downloaded from KSeF yet")}
          </Typography>
        )}
        {resumeScheduled && status.resumes_at && (
          <Typography variant="body2" color="text.secondary">
            {t("Download resumes automatically at {{date}}", {
              date: new Date(status.resumes_at).toLocaleString(),
            })}
          </Typography>
        )}
        {status.last_error && !status.in_progress && (
          <Alert severity="error">
            {t("Last download failed: {{error}}", { error: status.last_error })}
          </Alert>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      {downloadState}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Toolbar actions={actions} noTopPadding />
      </Box>
    </Box>
  );
};
